'use client';
import NextLink from 'next/link';
import { useState, useEffect, useMemo, useCallback } from 'react';

import { navigationLinks, NavigationLinks } from './navigationLinks';
import { FooterProps } from './types';

import IconChevron from '@/assets/icons/iconChevron.svg';
import IconFeedback from '@/assets/icons/iconFeedback.svg';
import IconLink from '@/assets/icons/iconLink.svg';
import IconLogo from '@/assets/icons/iconLogo.svg';
import IconLogoEng from '@/assets/icons/iconLogoEng.svg';
import IconMail from '@/assets/icons/iconMail.svg';
import IconPhone from '@/assets/icons/iconPhone.svg';
import IconTelegram from '@/assets/icons/iconTelegram.svg';
import IconVC from '@/assets/icons/iconVC.svg';
import { Link } from '@/shared/components/link';
import { SCREEN_SM } from '@/shared/constants/common';
import { classNames } from '@/shared/lib/classNames/classNames';

export const Footer = ({ translations, locale }: FooterProps) => {
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>();
  const [isFirefoxOrSafari, setIsFirefoxOrSafari] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === undefined) {
      return;
    }

    const handleResize = () => setIsMobile(window.innerWidth <= SCREEN_SM);
    setIsFirefoxOrSafari(
      navigator.userAgent.includes('Firefox') ||
        navigator.userAgent.includes('Safari'),
    );

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const openGroup = (groupName: string) => {
    setOpenGroups((openGroups) =>
      openGroups.includes(groupName)
        ? openGroups.filter((openedMenu) => openedMenu !== groupName)
        : [...openGroups, groupName],
    );
  };

  const renderLinksGroup = useCallback(
    (group: NavigationLinks, index: number) => {
      const groupLinks = group.links?.map((link, index) => {
        const linkName = link.href ? (
          <Link className="mb-2" href={`${translations[link.href]}`}>
            <div className="flex">
              {link.name && translations[link.name]} <IconLink />
            </div>
          </Link>
        ) : (
          <p className="text-text-base-secondary mb-2">
            {link.name && translations[link.name]}
          </p>
        );

        return (
          <div key={index} className="flex flex-col gap-2 sm:max-w-[180px]">
            {link.name && translations[link.name] && linkName}
            {link.name === 'footerFooterMenuSectionsSocialTitle' && (
              <div className="flex gap-3">
                <NextLink href="https://vc.ru/id808769" target="_blank">
                  <IconVC className="w-[34px] cursor-pointer" />
                </NextLink>

                <NextLink
                  href={
                    translations.footerFooterMenuSectionsSocialLinksTelegramLink
                  }
                  target="_blank"
                >
                  <IconTelegram className="w-[34px] cursor-pointer" />
                </NextLink>
              </div>
            )}
            {link.sublinks?.map((sublink, index) => (
              <Link
                key={index}
                variant="small"
                className="last:mb-5"
                href={`${translations[sublink.href] || sublink.href}`}
              >
                {translations[sublink.name]}
              </Link>
            ))}
          </div>
        );
      });

      return (
        <div
          key={index}
          style={{
            minWidth: isFirefoxOrSafari && index === 0 ? '380px' : 'auto',
          }}
        >
          <p
            className="mb-5 flex items-center justify-between text-lg max-sm:mb-[10px] max-sm:h-10"
            onClick={() => isMobile && openGroup(group.name)}
          >
            {translations[group.name]}
            {isMobile && (
              <IconChevron
                className={classNames({
                  mods: {
                    'rotate-180': openGroups.includes(group.name),
                  },
                })}
              />
            )}
          </p>
          <div
            className={classNames({
              cls: 'flex flex-col flex-wrap gap-x-20',
              mods: {
                hidden: !openGroups.includes(group.name) && isMobile,
                'min-[1168px]:h-80': index === 0,
              },
            })}
          >
            {groupLinks}
          </div>
        </div>
      );
    },
    [isMobile, openGroups, translations, isFirefoxOrSafari],
  );

  const filterNavigationLinks = useCallback(
    (links: Record<string, any>[]) =>
      links
        .map((link) => {
          const filteredLink = { ...link };

          if (
            !translations[link.name] &&
            !filteredLink.link &&
            !filteredLink.sublinks
          )
            return null;

          if (filteredLink.links)
            filteredLink.links = filterNavigationLinks(filteredLink.links);

          if (filteredLink.sublinks)
            filteredLink.sublinks = filterNavigationLinks(
              filteredLink.sublinks,
            );

          return filteredLink;
        })
        .filter(Boolean),
    [translations],
  );

  const links = useMemo(
    () =>
      (filterNavigationLinks(navigationLinks) as NavigationLinks[]).map(
        renderLinksGroup,
      ),
    [filterNavigationLinks, renderLinksGroup],
  );

  return (
    <footer className="bg-surface-base-secondary w-full">
      <section className="m-auto box-content max-w-[1168px] p-4">
        {locale === 'ru' ? (
          <IconLogo className="mb-10 w-36" />
        ) : (
          <IconLogoEng className="mb-10 w-36" />
        )}
        <div
          className={classNames({
            cls: 'flex flex-wrap max-sm:flex-col',
            mods: {
              'gap-x-20': locale !== 'am',
              'gap-x-15.5': locale === 'am',
            },
          })}
          itemScope
          itemType="http://schema.org/SiteNavigationElement"
        >
          <meta itemProp="name" content="Футер сайта" />
          {links}
        </div>
      </section>

      <section className="bg-surface-base-tertiary flex min-h-32 w-full items-center justify-center p-4">
        <div className="flex h-full w-[1168px] flex-wrap-reverse items-center justify-between gap-6">
          <span className="text-text-base-secondary">
            {translations.footerFooterMenuCopyright}
          </span>
          <div>
            <p className="text-text-base-secondary flex-grow">
              {translations.footerFooterMenuSectionsContactsTitle}
            </p>
            <div className="mt-3 flex flex-wrap gap-3 max-sm:flex-col">
              <p
                className="text-text-base-tertiary hover:text-status-accent cursor-pointer text-sm"
                onClick={() => {
                  //@ts-ignore
                  window.jivo_api?.open();
                }}
              >
                <IconFeedback className="mr-1 inline w-6" />
                {translations.footerFooterMenuSectionsContactsItemsChatName}
              </p>
              <Link
                variant="small"
                href={`tel:${translations.footerFooterMenuSectionsContactsItemsPhoneName?.replace(/\s|-/, '')}`}
              >
                <IconPhone className="mr-1 inline w-6" />
                {translations.footerFooterMenuSectionsContactsItemsPhoneName}
              </Link>
              <Link variant="small" href="mailto:dev@2gis.ru">
                <IconMail className="mr-1 inline w-6" />
                {translations.footerFooterMenuSectionsContactsItemsEmailName}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
};
